// Master Data API Endpoints Configuration
import { ENV_CONFIG } from '../config/environment';

export const MASTER_DATA_CONFIG = {
  // Base URL for master data APIs - uses centralized environment configuration
  BASE_URL: ENV_CONFIG.MASTER_DATA_API_BASE_URL,
  
  // API endpoints with their configurations
  ENDPOINTS: {
    // Test endpoint to verify API connectivity
    test: {
      path: '/test',
      name: 'Test Endpoint',
      description: 'Test endpoint to verify API connectivity',
      supportsPagination: false,
      defaultLimit: 1
    },
    
    languages: {
      path: '/language_lists',
      name: 'Languages',
      description: 'Mother tongue and language options',
      supportsPagination: true,
      defaultLimit: 10
    },
    
    maritalStatuses: {
      path: '/marital_statuses',
      name: 'Marital Statuses',
      description: 'Marital status options',
      supportsPagination: true,
      defaultLimit: 10
    },
    
    religions: {
      path: '/religion_lists',
      name: 'Religions',
      description: 'Religion options',
      supportsPagination: true,
      defaultLimit: 10
    },
    
    castes: {
      path: '/caste_lists',
      name: 'Castes',
      description: 'Caste options with division code support',
      supportsPagination: true,
      defaultLimit: 10,
      supportsDivisionCode: true,
      divisionCodeParam: 'division_code'
    },
    
    heights: {
      path: '/height_lists',
      name: 'Heights',
      description: 'Height options in centimeters',
      supportsPagination: true,
      defaultLimit: 10
    },
    
    birthStars: {
      path: '/birth_stars',
      name: 'Birth Stars',
      description: 'Astrological birth star options',
      supportsPagination: true,
      defaultLimit: 10
    },
    
    weights: {
      path: '/weight_lists',
      name: 'Weight Lists',
      description: 'Weight options',
      supportsPagination: true,
      defaultLimit: 10
    },
    
    manglikLists: {
      path: '/manglik_lists',
      name: 'Manglik Lists',
      description: 'Manglik/Dosham options',
      supportsPagination: true,
      defaultLimit: 10
    },
    
    jobs: {
      path: '/job_lists',
      name: 'Jobs',
      description: 'Professional job options',
      supportsPagination: true,
      defaultLimit: 10
    },
    
    educations: {
      path: '/educations',
      name: 'Educations',
      description: 'Educational qualification options',
      supportsPagination: true,
      defaultLimit: 10
    },
    
    interests: {
      path: '/interests',
      name: 'Interests',
      description: 'Personal interest and hobby options',
      supportsPagination: true,
      defaultLimit: 10
    },
    
    food: {
      path: '/food',
      name: 'Food Preferences',
      description: 'Food preference options',
      supportsPagination: true,
      defaultLimit: 10
    },
    
    drinking: {
      path: '/drinking',
      name: 'Drinking Preferences',
      description: 'Drinking habit options',
      supportsPagination: true,
      defaultLimit: 10
    },
    
    smoking: {
      path: '/smoking',
      name: 'Smoking Preferences',
      description: 'Smoking habit options',
      supportsPagination: true,
      defaultLimit: 10
    },
    
    ideologies: {
      path: '/ideologies',
      name: 'Ideologies & Beliefs',
      description: 'Ideological and belief system options',
      supportsPagination: true,
      defaultLimit: 10
    },
    
    familyTypes: {
      path: '/family_types',
      name: 'Family Types',
      description: 'Family status and type options',
      supportsPagination: true,
      defaultLimit: 10
    }
  },
  
  // Pagination settings
  PAGINATION: {
    DEFAULT_PAGE: 1,
    DEFAULT_LIMIT: 10,
    MAX_PAGES: 50, // Maximum pages to fetch when loading all data
    LOAD_MORE_THRESHOLD: 0.8 // Load more when user scrolls to 80% of the list
  },
  
  // API response structure expectations
  RESPONSE_STRUCTURE: {
    DATA_FIELD: 'data', // Field containing the actual data array
    TOTAL_FIELD: 'total', // Field containing total count (fallback)
    COUNT_FIELD: 'count', // Field containing total count (primary)
    PAGE_FIELD: 'page', // Field containing current page
    LIMIT_FIELD: 'limit', // Field containing items per page
    HAS_MORE_FIELD: 'hasMore' // Field indicating if more data exists
  },
  
  // Error handling
  ERROR_MESSAGES: {
    FETCH_FAILED: 'Failed to fetch data',
    NETWORK_ERROR: 'Network error occurred',
    INVALID_RESPONSE: 'Invalid response format',
    PAGINATION_ERROR: 'Error loading more data'
  }
};

// Helper function to get endpoint configuration
export const getEndpointConfig = (endpointKey) => {
  return MASTER_DATA_CONFIG.ENDPOINTS[endpointKey] || null;
};

// Helper function to build full URL for an endpoint
export const buildEndpointUrl = (endpointKey, options = {}) => {
  const config = getEndpointConfig(endpointKey);
  if (!config) {
    throw new Error(`Unknown endpoint: ${endpointKey}`);
  }
  
  const { page = 1, limit = 10, params = {}, search = '' } = options;
  
  // Build query parameters
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...params
  });
  
  // Add search parameter if provided
  if (search && search.trim()) {
    queryParams.append('search', search.trim());
  }
  
  return `${MASTER_DATA_CONFIG.BASE_URL}${config.path}?${queryParams.toString()}`;
};

// Helper function to build search URL for an endpoint
export const buildSearchEndpointUrl = (endpointKey, options = {}) => {
  const config = getEndpointConfig(endpointKey);
  if (!config) {
    throw new Error(`Unknown endpoint: ${endpointKey}`);
  }
  
  const { page = 1, limit = 10, params = {}, search = '' } = options;
  
  // Build query parameters
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...params
  });
  
  // Add search parameter if provided
  if (search && search.trim()) {
    queryParams.append('search', search.trim());
  }
  
  // Use search endpoint path
  return `${MASTER_DATA_CONFIG.BASE_URL}${config.path}/search?${queryParams.toString()}`;
};

// Helper function to check if endpoint supports pagination
export const supportsPagination = (endpointKey) => {
  const config = getEndpointConfig(endpointKey);
  return config ? config.supportsPagination : false;
};

// Helper function to get default limit for an endpoint
export const getDefaultLimit = (endpointKey) => {
  const config = getEndpointConfig(endpointKey);
  return config ? config.defaultLimit : MASTER_DATA_CONFIG.PAGINATION.DEFAULT_LIMIT;
};
